import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC } from 'react'
import { SlugifiedAttributesFilterValue } from 'services/providers/bapi/entities/filters/normalize'
import styled, { css } from 'styled-components'
import {
  complementaryColor,
  inactiveColor,
  primaryColor,
} from 'uiComponents/constants/colors'
import { fwBold } from 'uiComponents/constants/typography'
import FilterLabel from './FilterLabel'

const TabContainer = styled.div<{ reducedSizeVersion?: boolean }>`
  position: relative;
  width: 100%;
  padding: ${props => (props.reducedSizeVersion ? '0 8px' : '0 30px')};
  background: ${complementaryColor};
  box-shadow: 0 10px 15px 0 rgba(0, 0, 0, 0.04), 0 2px 2px 0 rgba(0, 0, 0, 0.08);
`

const TabSwitch = styled.ul<{ reducedSizeVersion?: boolean }>`
  padding-top: ${props => (props.reducedSizeVersion ? '12px' : '20px')};
  list-style: none;
  display: flex;
`

const StyledTabItem = styled.li<{ selected: boolean }>`
  display: inline-block;
  margin-right: 20px;
  text-align: center;
  ${fwBold};
  color: ${props => (props.selected ? primaryColor : inactiveColor)};
  cursor: pointer;

  :last-child {
    margin-right: 0;
  }

  :hover {
    color: ${primaryColor};
  }
`

const SelectedTabMarker = styled.div<{ selected: boolean }>`
  height: 17px;
  width: 20px;
  margin: auto;
  ${props =>
    props.selected &&
    css`
      border-bottom: 2px solid ${primaryColor};
    `};
`

interface TabSelectorProps {
  name?: string
  slug: string
  options: SlugifiedAttributesFilterValue[]
  selectedValues: string[]
  onReset: () => void
  selectOption: (id: string) => void
  defaultTabName?: string
  reducedSizeVersion?: boolean
}

const TabSelector: FC<TabSelectorProps> = ({
  name,
  slug,
  options,
  selectedValues,
  onReset,
  selectOption,
  defaultTabName,
  reducedSizeVersion = false,
}) => {
  const { t } = useTranslation()

  if (options.length === 0) {
    return null
  }

  return (
    <TabContainer
      data-testid={`Section_${slug}`}
      reducedSizeVersion={reducedSizeVersion}
    >
      {name && <FilterLabel>{name}</FilterLabel>}
      <TabSwitch reducedSizeVersion={reducedSizeVersion}>
        <TabItem
          key={'TabItem_all'}
          data-testid={`TabItem_all`}
          selected={selectedValues.length === 0}
          onClick={() => onReset()}
          label={defaultTabName ?? t('Alle')}
        />

        {options.map(option => {
          return (
            <TabItem
              key={option.id}
              data-testid={`TabItem_${option.id}`}
              selected={selectedValues.includes(option.id)}
              onClick={() => selectOption(option.id)}
              label={option.name}
            />
          )
        })}
      </TabSwitch>
    </TabContainer>
  )
}

export default TabSelector

const TabItem: FC<{
  selected: boolean
  label: string
  onClick: () => void
}> = ({ selected, label, onClick }) => {
  return (
    <StyledTabItem onClick={onClick} selected={selected}>
      {label}
      <SelectedTabMarker selected={selected} />
    </StyledTabItem>
  )
}
